import React from 'react'

import { MerchantLogo } from '@mxenabled/mxui'

import { observer } from 'mobx-react-lite'

import { BudgetDetailsTopSection } from './shared/BudgetDetailsTopSection'

import { getRemainingAmountSpentPercentage } from '../../core/utils/BudgetUtil'

import { useGlobalCopyStore, useTransactionStore } from '../../common'
import { type Transaction } from '../../core'
import { Drawer } from '../../common/components'
import type { MerchantBudget } from '../../core/types/Budget'
import { TransactionList } from '../../transactions'
import { CurrencyDialogAction } from '../../transactions/components/shared/transactiondetails/actions/AddMerchantBudgetAction'
import { buildDynamicCopy } from '../../core/utils/Localization'

interface MerchantBudgetDetailsDrawerProps {
  merchantBudget: MerchantBudget
  transaction: Transaction
  onClose: () => void
  isOpen: boolean
  setCurrencyDialogAction: (action: CurrencyDialogAction) => void
  renderingToast?: () => React.ReactNode
}

export const MerchantBudgetDetailsDrawer = ({
  merchantBudget,
  transaction,
  onClose,
  isOpen,
  setCurrencyDialogAction,
  renderingToast,
}: MerchantBudgetDetailsDrawerProps) => {
  const { budgets: budgetsCopy, common: commonCopy } = useGlobalCopyStore()

  const { sortedTransactions } = useTransactionStore()

  const transactionTotal = React.useMemo(() => {
    return sortedTransactions.reduce((acc, t) => {
      if (!t.parent_guid) {
        return acc + t.amount
      }
      return acc
    }, 0)
  }, [sortedTransactions])

  const { remainingAmount, spentPercentage, iconColor } = getRemainingAmountSpentPercentage(
    transactionTotal,
    merchantBudget.amount ?? 0,
  )

  return (
    <Drawer
      ariaLabelClose={commonCopy.close_aria}
      footerStyles={{
        px: 24,
        py: 16,
      }}
      isOpen={isOpen}
      onClose={onClose}
      onPrimaryAction={() => setCurrencyDialogAction(CurrencyDialogAction.EDIT)}
      onSecondaryAction={() => {
        setCurrencyDialogAction(CurrencyDialogAction.DELETE)
      }}
      primaryText={budgetsCopy.details_edit_button}
      secondaryColor="error"
      secondaryText={budgetsCopy.details_delete_button}
      sx={{
        '& .list-wrapper': {
          pr: 24,
        },
      }}
      title={
        buildDynamicCopy(budgetsCopy.details_type_title, merchantBudget.merchant_name) as string
      }
    >
      <BudgetDetailsTopSection
        amount={merchantBudget.amount}
        icon={
          <MerchantLogo
            categoryGuid={transaction.top_level_category_guid || ''}
            merchantGuid={transaction.merchant_guid || ''}
          />
        }
        iconColor={iconColor}
        remainingAmount={remainingAmount}
        spentPercentage={spentPercentage}
        startDate={transaction.date}
        totalTransaction={transactionTotal}
      />
      <TransactionList />
      {renderingToast?.()}
    </Drawer>
  )
}

export default observer(MerchantBudgetDetailsDrawer)
